import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    const token = localStorage.getItem("cbr_access_token");
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      localStorage.removeItem("cbr_access_token");
      localStorage.removeItem("cbr_refresh_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("cbr_access_token", data.access_token);
      localStorage.setItem("cbr_refresh_token", data.refresh_token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e?.response?.data?.detail) || e.message };
    }
  };

  const register = async (email, password, name) => {
    try {
      const { data } = await api.post("/auth/register", { email, password, name });
      localStorage.setItem("cbr_access_token", data.access_token);
      localStorage.setItem("cbr_refresh_token", data.refresh_token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e?.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    localStorage.removeItem("cbr_access_token");
    localStorage.removeItem("cbr_refresh_token");
    setUser(null);
  };

  const applySession = ({ access_token, refresh_token, user: u }) => {
    if (access_token) localStorage.setItem("cbr_access_token", access_token);
    if (refresh_token) localStorage.setItem("cbr_refresh_token", refresh_token);
    if (u) setUser(u);
  };

  const isAdmin = user && (user.role === "admin" || user.role === "super_admin");
  const isStaff = user && ["admin", "super_admin", "evaluator", "scout"].includes(user.role);
  const isEvaluator = user && (user.role === "evaluator" || user.role === "admin" || user.role === "super_admin");
  const isPlayer = user && user.role === "player";
  const isRecruiter = user && user.role === "recruiter";

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, applySession, refreshMe: fetchMe, isAdmin, isStaff, isEvaluator, isPlayer, isRecruiter }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
